import AsyncStorage from '@react-native-async-storage/async-storage';
import { getCurrentOrganization } from './organizationService';
import type {
  AppPreferences,
  OrganizationLocalization,
  ThemePreference,
} from '../types/settings';

const PREFERENCES_KEY = 'arkhrms.app.preferences';

const DEFAULT_PREFERENCES: AppPreferences = {
  theme: 'system',
};

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function getDefaultPreferences(): AppPreferences {
  return { ...DEFAULT_PREFERENCES };
}

export async function getPreferences(): Promise<AppPreferences> {
  try {
    const raw = await AsyncStorage.getItem(PREFERENCES_KEY);
    if (!raw) return getDefaultPreferences();
    const parsed = JSON.parse(raw) as Partial<AppPreferences>;
    return {
      theme: isThemePreference(parsed.theme) ? parsed.theme : DEFAULT_PREFERENCES.theme,
    };
  } catch {
    return getDefaultPreferences();
  }
}

export async function savePreferences(
  next: Partial<AppPreferences>,
): Promise<AppPreferences> {
  const current = await getPreferences();
  const merged: AppPreferences = {
    theme: isThemePreference(next.theme) ? next.theme : current.theme,
  };
  await AsyncStorage.setItem(PREFERENCES_KEY, JSON.stringify(merged));
  return merged;
}

export async function setThemePreference(
  theme: ThemePreference,
): Promise<AppPreferences> {
  return savePreferences({ theme });
}

/** Organization localization defaults (SETTINGS_VIEW). Not user-editable here. */
export async function getOrganizationLocalization(): Promise<OrganizationLocalization> {
  const organization = await getCurrentOrganization();
  return {
    timeZone: organization.timeZone || null,
    defaultDateFormat: organization.defaultDateFormat || null,
    defaultTimeFormat: organization.defaultTimeFormat || null,
  };
}
